import * as React from 'react';
// @ts-expect-error
import type {ContextRouter} from 'react-router-dom';

import {safelyGetFilterByKey} from 'nutshell-core/filter';
import type {FilterObject} from 'nutshell-core/types';

import {SaveButton} from 'shells/button/save-button/save-button';
import {ListPageControlBar} from 'shells/control-bar/list-page-control-bar';
import {ViewBoardIcon as BoardIcon} from 'shells/icon/svg/view-board-icon';
import {TabbedContent} from 'shells/layout/tabbed-content';
import {SingleInputModal} from 'shells/single-input-modal/single-input-modal';

import {PageContainer, PageTitleBar} from '../../../layout';

import {appHistory} from '../../../../history';
import {writeLocalStorage} from '../../../../util/localStorage';

import {DripSequencesBoardView} from './drip-sequences-board-view';
import {
    useCreateSmsDripSequence,
    useGetSmsDripSequences,
    useDeleteSmsDripSequence,
    useRestoreSmsDripSequence,
    useDuplicateSmsDripSequence,
} from './graphql/hooks';

const SMS_DRIP_SEQUENCES_VIEW_SEARCH = 'SMS_DRIP_SEQUENCES_VIEW_SEARCH';

type Props = ContextRouter & {
    urlParams: any;
    getUpdatedUrl: (arg1: any) => string;
    activeFilters: FilterObject[];
    onUpdateFilters: (arg1: FilterObject) => void;
};

export function DripSequencesListPage(props: Props) {
    const [isSavingNewDripSequence, setIsSavingNewDripSequence] = React.useState<boolean>(false);
    const [duplicatingDripSequenceId, setDuplicatingDripSequenceId] = React.useState<
        string | null | undefined
    >(null);

    const {deleteCampaign} = useDeleteSmsDripSequence();
    const {restoreCampaign} = useRestoreSmsDripSequence();
    const {duplicateDripSequence} = useDuplicateSmsDripSequence();

    const {createDripSequence, isCreating} = useCreateSmsDripSequence();

    const isShowingArchived = safelyGetFilterByKey(props.activeFilters, 'archived') === 'true';
    const {smsDripSequences, isLoading, error} = useGetSmsDripSequences({
        archived: isShowingArchived,
    });
    const selectedTab = props.urlParams.view || 'board';

    React.useEffect(() => {
        writeLocalStorage(SMS_DRIP_SEQUENCES_VIEW_SEARCH, props.location.search);
    }, [props.location.search]);

    const onCreateDripSequence = () => {
        createDripSequence({}).then((result) => {
            if (result && result.data && result.data.smsDripSequenceCreate) {
                appHistory.push(result.data.smsDripSequenceCreate.dripSequence.htmlUrlPath);
            }
        });
    };

    const duplicatingDripSequence = smsDripSequences.find(
        (sequence) => sequence.id === duplicatingDripSequenceId
    );

    const tabOptions = [
        {
            label: 'Board',
            id: 'board',
            icon: BoardIcon,
            href: props.getUpdatedUrl({view: 'board'}),
            component: (
                <DripSequencesBoardView
                    dripSequences={smsDripSequences}
                    error={error}
                    onCreateNewDripSequence={onCreateDripSequence}
                    isCreating={isCreating}
                    isFiltering={false}
                    isLoading={isLoading}
                    isShowingArchived={isShowingArchived}
                    onDeleteDripSequence={deleteCampaign}
                    onRestoreDripSequence={restoreCampaign}
                    onDuplicateDripSequence={(id) => setDuplicatingDripSequenceId(id)}
                />
            ),
        },
    ];

    return (
        <>
            <PageContainer
                title='SMS drip sequences'
                pageTitleBar={
                    <PageTitleBar
                        title='SMS drip sequences'
                        subheaderPrimaryText='Automatically send text messages at the right moment'
                        buttons={
                            <SaveButton
                                variant='primary'
                                size='big'
                                // @ts-expect-error
                                onClick={onCreateDripSequence}
                                isSaving={isCreating}
                                buttonText='Create new drip sequence'
                                savingText='Creating drip sequence'
                                successText='Drip sequence created'
                            />
                        }
                    />
                }
                controlBar={
                    <ListPageControlBar
                        tabs={{
                            selectedTab: selectedTab,
                            options: tabOptions,
                        }}
                        archiveTab={{
                            isActive: isShowingArchived,
                            onChange: () => {
                                props.onUpdateFilters({
                                    archived: isShowingArchived ? null : true,
                                });
                            },
                        }}
                    />
                }
            >
                {/* @ts-expect-error */}
                <TabbedContent selectedTab={selectedTab} tabOptions={tabOptions} />
            </PageContainer>
            {duplicatingDripSequence && (
                <SingleInputModal
                    requireValue={true}
                    buttonText='Make a copy'
                    title='Name this drip sequence'
                    subtitle='Add a descriptive name so you can find it later'
                    initialValue={`Copy of ${duplicatingDripSequence.name}`}
                    isSaving={isSavingNewDripSequence}
                    onSave={(name) => {
                        setIsSavingNewDripSequence(true);

                        duplicateDripSequence(name, duplicatingDripSequence.id).then(
                            (duplicateData) => {
                                setDuplicatingDripSequenceId(null);
                                setIsSavingNewDripSequence(false);
                                if (
                                    duplicateData &&
                                    duplicateData.data &&
                                    duplicateData.data.dripSequenceDuplicate
                                ) {
                                    appHistory.push(
                                        duplicateData.data.dripSequenceDuplicate.dripSequence
                                            .htmlUrlPath
                                    );
                                }
                            }
                        );
                    }}
                    onClose={() => {
                        setDuplicatingDripSequenceId(null);
                    }}
                />
            )}
        </>
    );
}
